import type {
  ActorRole,
  FilterOptions,
  FilterOption,
  SlownessBucket,
} from "@gw/shared-types";
import { DIVISION_HINT, MANAGER_HINT, OWNER_HINT, TEAM_HINT } from "@/features/shared/ownerGroupLabels";
import { FilterRail } from "@/features/requestList/components/FilterRail";
import { FilterGroup } from "@/features/requestList/components/FilterGroup";
import { DateRangeFilter } from "@/features/requestList/components/DateRangeFilter";
import { slownessLabel } from "@/lib/labels";
import type { TaskEfficiencyState } from "../taskEfficiencySchema";
import { TASK_DEFAULTS } from "../taskEfficiencySchema";

// Four derived statuses — same rule the Detail screen's pills use (action-timestamp based).
// Raw sproc Status is intentionally NOT exposed: it can lag the actual state.
const TASK_STATUS_OPTIONS: FilterOption[] = [
  { value: "Completed", count: 0 },
  { value: "InProgress", count: 0 },
  { value: "NotStarted", count: 0 },
  { value: "Discarded", count: 0 },
];

const TASK_STATUS_LABELS: Record<string, string> = {
  Completed: "Completed",
  InProgress: "In progress",
  NotStarted: "Not started",
  Discarded: "Discarded",
};

interface Props {
  state: TaskEfficiencyState;
  setState: (patch: Partial<TaskEfficiencyState>) => void;
  filterOptions: FilterOptions | undefined;
  /** Distinct task category/name + actor role options derived from the current snapshot. */
  taskCategoryOptions: FilterOption[];
  taskNameOptions: FilterOption[];
  actorRoleOptions: FilterOption[];
  onClearAll: () => void;
}

export function TaskFilterRail({
  state,
  setState,
  filterOptions,
  taskCategoryOptions,
  taskNameOptions,
  actorRoleOptions,
  onClearAll,
}: Props) {
  return (
    <FilterRail onClear={onClearAll} storageKey="tasks">
      <DateRangeFilter
        dateRange={state.dateRange}
        from={state.from}
        to={state.to}
        defaultDateRange={TASK_DEFAULTS.dateRange}
        onChange={(patch) => setState({ ...patch, page: 1 })}
      />

      <FilterGroup
        label="Status"
        options={TASK_STATUS_OPTIONS}
        selected={state.taskStatus}
        onChange={(arr) => setState({ taskStatus: arr as TaskEfficiencyState["taskStatus"], page: 1 })}
        formatValue={(v) => TASK_STATUS_LABELS[v] ?? v}
      />

      {taskCategoryOptions.length > 0 && (
        <FilterGroup
          label="Task category"
          options={taskCategoryOptions}
          selected={state.taskCategory}
          onChange={(arr) => setState({ taskCategory: arr, page: 1 })}
        />
      )}
      {taskNameOptions.length > 0 && (
        <FilterGroup
          label="Task name"
          options={taskNameOptions}
          selected={state.taskName}
          onChange={(arr) => setState({ taskName: arr, page: 1 })}
        />
      )}
      {actorRoleOptions.length > 0 && (
        <FilterGroup
          label="Actor role"
          options={actorRoleOptions}
          selected={state.actorRole}
          onChange={(arr) => setState({ actorRole: arr as ActorRole[], page: 1 })}
        />
      )}

      {filterOptions && (
        <>
          <FilterGroup
            label="Slowness"
            options={filterOptions.slownessBuckets}
            selected={state.bucket}
            onChange={(arr) => setState({ bucket: arr as SlownessBucket[], page: 1 })}
            formatValue={(v) => slownessLabel(v as SlownessBucket)}
          />
          <FilterGroup
            label="Division"
            hint={DIVISION_HINT}
            options={filterOptions.divisions ?? []}
            selected={state.division}
            onChange={(arr) => setState({ division: arr, page: 1 })}
          />
          <FilterGroup
            label="Manager"
            hint={MANAGER_HINT}
            options={filterOptions.managers ?? []}
            selected={state.manager}
            onChange={(arr) => setState({ manager: arr, page: 1 })}
          />
          <FilterGroup
            label="Team"
            hint={TEAM_HINT}
            options={filterOptions.teams ?? []}
            selected={state.team}
            onChange={(arr) => setState({ team: arr, page: 1 })}
            searchable
          />
          <FilterGroup
            label="Owner"
            hint={OWNER_HINT}
            options={filterOptions.assignedUsers}
            selected={state.assigned}
            onChange={(arr) => setState({ assigned: arr, page: 1 })}
          />
          <FilterGroup
            label="Request type"
            max={100}
            options={filterOptions.requestTypes.filter(
              (o) => o.value.toLowerCase() !== "onboarding",
            )}
            selected={state.requestType}
            onChange={(arr) => setState({ requestType: arr, page: 1 })}
            extraOptions={[
              {
                label: "Onboarding",
                checked: state.onboardingsOnly,
                onToggle: () =>
                  setState({ onboardingsOnly: !state.onboardingsOnly, page: 1 }),
              },
              {
                label: "Redeployment",
                checked: state.redeployment,
                onToggle: () => setState({ redeployment: !state.redeployment, page: 1 }),
              },
            ]}
          />
          <FilterGroup
            label="Client"
            options={filterOptions.clients}
            selected={state.client}
            onChange={(arr) => setState({ client: arr, page: 1 })}
          />
          <FilterGroup
            label="Line of business"
            options={filterOptions.linesOfBusiness}
            selected={state.lob}
            onChange={(arr) => setState({ lob: arr, page: 1 })}
          />
          <FilterGroup
            label="Engagement type"
            options={filterOptions.engagementTypes}
            selected={state.eng}
            onChange={(arr) => setState({ eng: arr, page: 1 })}
          />
          <FilterGroup
            label="Country"
            options={filterOptions.workerCountries}
            selected={state.country}
            onChange={(arr) => setState({ country: arr, page: 1 })}
          />
        </>
      )}

      <div className="filter-group">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={state.rework}
            onChange={() => setState({ rework: !state.rework, page: 1 })}
            style={{ accentColor: "var(--gw-primary-500)", width: 14, height: 14 }}
          />
          Has rework (rejected ≥ 1)
        </label>
      </div>
    </FilterRail>
  );
}
